
import { defineComponent, ref, computed, onMounted, watch, nextTick } from 'vue';
import { gitBranchOutline } from 'ionicons/icons';

interface ContributionDay {
  date: string;
  count: number;
}

interface ActivityStats {
  totalContributions: number;
  longestStreak: number;
  currentStreak: number;
}

interface GitHubContributionData {
  contributions: ContributionDay[];
  stats?: ActivityStats;
}

interface CalendarDay extends ContributionDay {
  dayOfWeek: number;
  weekIndex: number;
}

export default defineComponent({
  name: 'LocalGithubActivity',
  props: {
    username: {
      type: String,
      required: true
    },
    apiUrl: {
      type: String,
      default: '/data/github-activity.json'
    },
    useGitHubApi: {
      type: Boolean,
      default: true
    },
    branding: {
      type: Boolean,
      default: true
    },
    maxContributions: {
      type: Number,
      default: undefined
    },
    gitlabBaseUrl: {
      type: String,
      default: ''
    },
    gitlabUsername: {
      type: String,
      default: ''
    }
  },
  setup(props) {
    // Reactive data
    const loading = ref(true);
    const error = ref<string | null>(null);
    const activityData = ref<ContributionDay[]>([]);
    const stats = ref<ActivityStats | null>(null);
    const tooltip = ref({ show: false, x: 0, y: 0, text: '' });
    const tooltipEl = ref<HTMLElement | null>(null);

    // Computed properties
    const showBranding = computed(() => props.branding);


    const calendarData = computed(() => {
      if (!activityData.value.length) return { firstHalf: [], secondHalf: [], full: [] };

      const today = new Date();
      const oneYearAgo = new Date(today);
      oneYearAgo.setFullYear(today.getFullYear() - 1);

      // Find the Sunday before one year ago
      const startDate = new Date(oneYearAgo);
      startDate.setDate(startDate.getDate() - startDate.getDay());

      // Create a data map for quick lookup
      const dataMap = new Map<string, number>();
      activityData.value.forEach(day => {
        dataMap.set(day.date, day.count);
      });

      const fullData: CalendarDay[] = [];
      const current = new Date(startDate);

      // Generate 53 weeks * 7 days = 371 days
      for (let i = 0; i < 371; i++) {
        const dateStr = current.toISOString().split('T')[0];
        const count = dataMap.get(dateStr) || 0;

        fullData.push({
          date: dateStr,
          count,
          dayOfWeek: current.getDay(),
          weekIndex: Math.floor(i / 7)
        });

        current.setDate(current.getDate() + 1);
      }

      // Split data into two periods, ensuring the second half starts on a Sunday
      const approximateMidpoint = Math.floor(fullData.length / 2);

      // Find the nearest Sunday to the midpoint
      let splitPoint = approximateMidpoint;
      // Look forward for the next Sunday (dayOfWeek === 0)
      while (splitPoint < fullData.length && fullData[splitPoint].dayOfWeek !== 0) {
        splitPoint++;
      }

      // Fallback: if no Sunday found after midpoint, look before
      if (splitPoint >= fullData.length) {
        splitPoint = approximateMidpoint;
        while (splitPoint > 0 && fullData[splitPoint].dayOfWeek !== 0) {
          splitPoint--;
        }
      }

      const firstHalf = fullData.slice(0, splitPoint).map((day, index) => ({
        ...day,
        weekIndex: Math.floor(index / 7)
      }));

      const secondHalf = fullData.slice(splitPoint).map((day, index) => ({
        ...day,
        weekIndex: Math.floor(index / 7)
      }));

      return { firstHalf, secondHalf, full: fullData };
    });

    const monthLabels = computed(() => {
      if (!calendarData.value.firstHalf.length) return { firstHalf: [], secondHalf: [], full: [] };

      const months = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

      const createLabels = (data: CalendarDay[], totalWeeks = 26) => {
        const labels: Array<{ month: number; label: string }> = [];
        const monthPositions: Array<{ month: number; weekIndex: number }> = [];
        let currentMonth = -1;

        // Collect all month positions
        data.forEach((day, index) => {
          const date = new Date(day.date);
          const month = date.getMonth();
          const weekIndex = Math.floor(index / 7);

          if (month !== currentMonth && date.getDate() <= 7) {
            currentMonth = month;
            monthPositions.push({ month, weekIndex });
          }
        });

        // Always include first and last months, plus evenly distributed ones in between
        if (monthPositions.length > 0) {
          // Always add first month
          labels.push({
            month: monthPositions[0].month,
            label: months[monthPositions[0].month]
          });

          // Add middle months if we have space
          if (monthPositions.length > 2) {
            const maxLabels = 4;
            const middleCount = Math.min(maxLabels - 2, monthPositions.length - 2);

            for (let i = 1; i <= middleCount; i++) {
              const index = Math.floor((i / (middleCount + 1)) * (monthPositions.length - 1));
              if (index > 0 && index < monthPositions.length - 1) {
                const pos = monthPositions[index];
                labels.push({
                  month: pos.month,
                  label: months[pos.month]
                });
              }
            }
          }

          // Always add last month if different from first
          if (monthPositions.length > 1) {
            const lastPos = monthPositions[monthPositions.length - 1];
            labels.push({
              month: lastPos.month,
              label: months[lastPos.month]
            });
          }
        }

        return labels;
      };

      return {
        firstHalf: createLabels(calendarData.value.firstHalf, 26),
        secondHalf: createLabels(calendarData.value.secondHalf, 26),
        full: createLabels(calendarData.value.full, 53)
      };
    });

    const formatDate = (dateStr: string): string => {
      try {
        const date = new Date(dateStr);
        return date.toLocaleDateString('en-US', {
          weekday: 'short',
          year: 'numeric',
          month: 'short',
          day: 'numeric'
        });
      } catch {
        return dateStr;
      }
    };

    const getTooltip = (day: CalendarDay): string => {
      if (!day || day.count === 0) {
        return `No contributions on ${formatDate(day.date)}`;
      }
      const plural = day.count === 1 ? 'contribution' : 'contributions';
      return `${day.count} ${plural} on ${formatDate(day.date)}`;
    };

    const getIntensityClass = (count: number): string => {
      if (count === 0) return 'intensity-0';
      if (count <= 2) return 'intensity-1';
      if (count <= 5) return 'intensity-2';
      if (count <= 8) return 'intensity-3';
      return 'intensity-4';
    };

    const showTooltip = async (event: MouseEvent, day: CalendarDay) => {
      tooltip.value.text = getTooltip(day);
      tooltip.value.show = true;

      await nextTick();

      if (tooltipEl.value) {
        const rect = (event.target as HTMLElement).getBoundingClientRect();
        const tooltipRect = tooltipEl.value.getBoundingClientRect();

        tooltip.value.x = rect.left + rect.width / 2 - tooltipRect.width / 2;
        tooltip.value.y = rect.top - tooltipRect.height - 8;

        // Ensure tooltip stays within viewport
        if (tooltip.value.x < 8) tooltip.value.x = 8;
        if (tooltip.value.x + tooltipRect.width > window.innerWidth - 8) {
          tooltip.value.x = window.innerWidth - tooltipRect.width - 8;
        }
        if (tooltip.value.y < 8) {
          tooltip.value.y = rect.bottom + 8;
        }
      }
    };

    const hideTooltip = () => {
      tooltip.value.show = false;
    };

    // Generate fallback data for the past year
    const generateFallbackData = (): ContributionDay[] => {
      const data: ContributionDay[] = [];
      const today = new Date();
      const oneYearAgo = new Date(today);
      oneYearAgo.setFullYear(today.getFullYear() - 1);

      // Generate sample data with realistic patterns
      for (let d = new Date(oneYearAgo); d <= today; d.setDate(d.getDate() + 1)) {
        const dayOfWeek = d.getDay();
        const isWeekend = dayOfWeek === 0 || dayOfWeek === 6;

        // Lower activity on weekends, some random gaps
        let count = 0;
        const random = Math.random();

        if (random > 0.3) { // 70% chance of having contributions
          if (isWeekend) {
            count = random > 0.7 ? Math.floor(Math.random() * 3) : 0;
          } else {
            count = Math.floor(Math.random() * 8) + 1;
          }
        }

        data.push({
          date: d.toISOString().split('T')[0],
          count
        });
      }

      return data;
    };

    const calculateStats = (data: ContributionDay[]): ActivityStats => {
      const totalContributions = data.reduce((sum, day) => sum + day.count, 0);

      // Calculate longest streak
      let longestStreak = 0;
      let currentStreakCount = 0;

      for (const day of data) {
        if (day.count > 0) {
          currentStreakCount++;
          longestStreak = Math.max(longestStreak, currentStreakCount);
        } else {
          currentStreakCount = 0;
        }
      }

      // Calculate current streak (from the end)
      let currentStreak = 0;
      for (let i = data.length - 1; i >= 0; i--) {
        if (data[i].count > 0) {
          currentStreak++;
        } else {
          break;
        }
      }

      return {
        totalContributions,
        longestStreak,
        currentStreak
      };
    };

    const fetchGitHubContributions = async (): Promise<GitHubContributionData> => {
      // Using GitHub's public contribution API endpoint
      // This endpoint provides contribution data without requiring authentication
      const contributionsUrl = `https://github-contributions-api.jogruber.de/v4/${props.username}?y=last`;

      try {
        const response = await fetch(contributionsUrl);
        if (!response.ok) {
          throw new Error(`GitHub API request failed: ${response.status}`);
        }

        const data = await response.json();

        // Transform the API response to our expected format
        const contributions: ContributionDay[] = [];

        if (data.contributions && Array.isArray(data.contributions)) {
          // The API returns contributions as a flat array
          data.contributions.forEach((day: any) => {
            contributions.push({
              date: day.date,
              count: day.count || 0
            });
          });
        }

        // Calculate stats from the contribution data
        const stats = calculateStats(contributions);

        return {
          contributions,
          stats
        };
      } catch (error) {
        // If the third-party API fails, try GitHub's own contribution calendar
        // This is a fallback approach using a different service
        const fallbackUrl = `https://github-contributions.vercel.app/api/v1/${props.username}`;

        try {
          const fallbackResponse = await fetch(fallbackUrl);
          if (!fallbackResponse.ok) {
            throw new Error(`Fallback API request failed: ${fallbackResponse.status}`);
          }

          const fallbackData = await fallbackResponse.json();

          const contributions: ContributionDay[] = [];

          if (fallbackData.contributions) {
            Object.entries(fallbackData.contributions).forEach(([date, count]) => {
              contributions.push({
                date,
                count: count as number
              });
            });
          }

          // Sort by date to ensure proper order
          contributions.sort((a, b) => a.date.localeCompare(b.date));

          const stats = calculateStats(contributions);

          return {
            contributions,
            stats
          };
        } catch (fallbackError) {
          // Both APIs failed, throw the original error
          throw error;
        }
      }
    };

    const fetchGitLabContributions = async (): Promise<ContributionDay[]> => {
      if (!props.gitlabBaseUrl || !props.gitlabUsername) return [];

      const url = `${props.gitlabBaseUrl}/users/${props.gitlabUsername}/calendar.json`;
      try {
        const response = await fetch(url);
        if (!response.ok) return [];

        const data = await response.json();
        // GitLab returns { "date": count, ... }
        const contributions: ContributionDay[] = [];
        Object.entries(data).forEach(([date, count]) => {
          contributions.push({ date, count: count as number });
        });
        return contributions;
      } catch {
        // GitLab fetch failed silently — GitHub data still works
        return [];
      }
    };

    const mergeContributions = (github: ContributionDay[], gitlab: ContributionDay[]): ContributionDay[] => {
      const byDate = new Map<string, number>();
      for (const c of github) {
        byDate.set(c.date, (byDate.get(c.date) || 0) + c.count);
      }
      for (const c of gitlab) {
        byDate.set(c.date, (byDate.get(c.date) || 0) + c.count);
      }
      return Array.from(byDate.entries())
        .map(([date, count]) => ({ date, count }))
        .sort((a, b) => a.date.localeCompare(b.date));
    };

    const fetchStaticData = async (): Promise<GitHubContributionData> => {
      const response = await fetch(props.apiUrl);
      if (!response.ok) {
        throw new Error(`Failed to load static data: ${response.status}`);
      }
      return await response.json();
    };

    const fetchData = async () => {
      try {
        loading.value = true;
        error.value = null;

        let data: GitHubContributionData;

        if (props.useGitHubApi) {
          try {
            data = await fetchGitHubContributions();
          } catch (apiError) {
            // GitHub API failed, falling back to static data
            try {
              data = await fetchStaticData();
              error.value = 'Using static data (GitHub API unavailable)';
            } catch (staticError) {
              // Static data failed, generating fallback
              data = {
                contributions: generateFallbackData()
              };
              error.value = 'Using generated sample data';
            }
          }
        } else {
          try {
            data = await fetchStaticData();
          } catch (staticError) {
            // Static data failed, generating fallback
            data = {
              contributions: generateFallbackData()
            };
            error.value = 'Using generated sample data';
          }
        }

        // Fetch GitLab contributions and merge
        const gitlabContributions = await fetchGitLabContributions();
        if (gitlabContributions.length > 0) {
          data.contributions = mergeContributions(data.contributions, gitlabContributions);
          data.stats = calculateStats(data.contributions);
        }

        activityData.value = data.contributions;
        stats.value = data.stats || calculateStats(data.contributions);

      } catch (err) {
        const fallbackData = generateFallbackData();
        activityData.value = fallbackData;
        stats.value = calculateStats(fallbackData);
        error.value = err instanceof Error ? err.message : 'Failed to load GitHub activity';
        // Log error for debugging
      } finally {
        loading.value = false;
      }
    };

    // Lifecycle
    onMounted(() => {
      fetchData();
    });

    // Watch for username changes
    watch(() => props.username, () => {
      fetchData();
    });

    return {
      gitBranchOutline,
      loading,
      error,
      activityData,
      stats,
      calendarData,
      monthLabels,
      showBranding,
      tooltip,
      tooltipEl,
      getTooltip,
      getIntensityClass,
      showTooltip,
      hideTooltip
    };
  }
});
